import { useEffect, useState, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';

// Aggiornamenti real-time + metriche demo (solo per altre statistiche)
interface RealTimeData {
  newBookings: number;
  onlineUsers: number;
  revenue: number;
  occupancyRate: number;
  lastUpdate: string;
  recentActivity: ActivityItem[];
}

interface ActivityItem {
  id: string;
  type: 'booking' | 'cancellation' | 'payment' | 'inquiry';
  message: string;
  timestamp: string;
  value?: number;
}

interface UseRealTimeUpdatesReturn {
  data: RealTimeData;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

// Hook per gestire gli aggiornamenti in tempo reale
export const useRealTimeUpdates = (): UseRealTimeUpdatesReturn => {
  const [data, setData] = useState<RealTimeData>({
    newBookings: 0,
    onlineUsers: 0,
    revenue: 0,
    occupancyRate: 0,
    lastUpdate: new Date().toISOString(),
    recentActivity: []
  });
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [socket, setSocket] = useState<Socket | null>(null);

  // Genera metriche demo NON legate agli utenti online (che arrivano dal server)
  const generateRandomUpdate = useCallback((): Partial<RealTimeData> => {
    const activityTypes: ActivityItem['type'][] = ['booking', 'cancellation', 'payment', 'inquiry'];
    const randomType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    
    const messages = {
      booking: [
        'Nuova prenotazione da Milano',
        'Prenotazione confermata da Firenze',
        'Booking internazionale da Londra',
        'Prenotazione last-minute da Roma'
      ],
      cancellation: [
        'Cancellazione prenotazione Pisa',
        'Rimborso elaborato per Venezia',
        'Cancellazione per maltempo'
      ],
      payment: [
        'Pagamento ricevuto €450',
        'Bonifico confermato €280',
        'Pagamento carta €190'
      ],
      inquiry: [
        'Richiesta info da Napoli',
        'Domanda disponibilità agosto',
        'Richiesta preventivo gruppo'
      ]
    };

    const newActivity: ActivityItem = {
      id: Date.now().toString(),
      type: randomType,
      message: messages[randomType][Math.floor(Math.random() * messages[randomType].length)],
      timestamp: new Date().toISOString(),
      value: randomType === 'payment' ? Math.floor(Math.random() * 500) + 100 : undefined
    };

    return {
      newBookings: Math.floor(Math.random() * 3),
      revenue: Math.floor(Math.random() * 1000) + 500,
      occupancyRate: Math.floor(Math.random() * 20) + 70,
      lastUpdate: new Date().toISOString(),
      recentActivity: [newActivity]
    };
  }, []);

  const getBackendUrl = (): string => {
    const envUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;
    if (envUrl) return envUrl;
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3001';
    return `${protocol}//${hostname}`;
  };

  // Connessione reale via Socket.IO per conteggio utenti online
  useEffect(() => {
    try {
      setConnectionStatus('connecting');
      const s = io(getBackendUrl(), {
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      setSocket(s);

      s.on('connect', () => {
        setIsConnected(true);
        setConnectionStatus('connected');
      });

      s.on('disconnect', () => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
      });

      s.on('connect_error', () => {
        setIsConnected(false);
        setConnectionStatus('error');
      });

      s.on('online-users', (count: number) => {
        setData(prev => ({
          ...prev,
          onlineUsers: typeof count === 'number' ? count : 0,
          lastUpdate: new Date().toISOString()
        }));
      });

      return () => {
        try { s.close(); } catch {}
        setIsConnected(false);
        setConnectionStatus('disconnected');
      };
    } catch {
      setConnectionStatus('error');
    }
  }, []);

  // Aggiorna periodicamente SOLO le altre metriche demo
  useEffect(() => {
    const updateInterval = setInterval(() => {
      const update = generateRandomUpdate();
      setData(prevData => ({
        ...prevData,
        ...update,
        // Non toccare onlineUsers qui (arriva dal server)
        recentActivity: [
          ...(update.recentActivity || []),
          ...prevData.recentActivity
        ].slice(0, 10)
      }));
    }, 5000);

    return () => clearInterval(updateInterval);
  }, [generateRandomUpdate]);

  return {
    data,
    isConnected,
    connectionStatus
  };
};

// Funzione per ottenere colore del status
export const getStatusColor = (status: UseRealTimeUpdatesReturn['connectionStatus']) => {
  switch (status) {
    case 'connected': return '#22c55e';
    case 'connecting': return '#eab308';
    case 'error': return '#ef4444';
    default: return '#6b7280';
  }
};

// Funzione per ottenere testo del status
export const getStatusText = (status: UseRealTimeUpdatesReturn['connectionStatus']) => {
  switch (status) {
    case 'connected': return '🟢 Connesso';
    case 'connecting': return '🟡 Connessione...';
    case 'error': return '🔴 Errore';
    default: return '⚫ Disconnesso';
  }
};

// Hook per gestire notifiche push
export const useNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = async () => {
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    }
    return 'denied';
  };

  const showNotification = (title: string, options?: NotificationOptions) => {
    if (permission === 'granted') {
      new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
    }
  };

  return {
    permission,
    requestPermission,
    showNotification,
    isSupported: 'Notification' in window
  };
};

// Utility per formattare i timestamps
export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return 'Ora';
  if (diffInMinutes < 60) return `${diffInMinutes}m fa`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h fa`;
  return date.toLocaleDateString('it-IT', { 
    day: '2-digit', 
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Simulazione API calls per i dati
export const generateSampleChartData = () => {
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      date: date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      bookings: Math.floor(Math.random() * 20) + 5,
      revenue: Math.floor(Math.random() * 2000) + 500,
      confirmed: Math.floor(Math.random() * 15) + 5,
      cancelled: Math.floor(Math.random() * 3)
    };
  });
  return last30Days;
};

export const generateSampleMetrics = () => ({
  conversionRate: Math.random() * 10 + 8, // 8-18%
  conversionChange: (Math.random() - 0.5) * 4, // -2 to +2%
  averageStayDuration: Math.random() * 3 + 2, // 2-5 notti
  stayChange: (Math.random() - 0.5) * 1, // -0.5 to +0.5 notti
  averageRating: Math.random() * 1 + 4, // 4-5 stelle
  ratingChange: (Math.random() - 0.5) * 0.4, // -0.2 to +0.2
  occupancyRate: Math.random() * 20 + 70, // 70-90%
  occupancyChange: (Math.random() - 0.5) * 10, // -5 to +5%
  repeatGuestRate: Math.random() * 15 + 20, // 20-35%
  repeatChange: (Math.random() - 0.5) * 6, // -3 to +3%
  averageBookingValue: Math.random() * 200 + 300, // €300-500
  bookingValueChange: (Math.random() - 0.5) * 100, // -50 to +50€
});

export const generateSampleLocationData = () => [
  { country: 'Italia', city: 'Roma', flag: '🇮🇹', bookings: 156, revenue: 78500, averageStay: 3.2, coordinates: [41.9028, 12.4964] as [number, number] },
  { country: 'Italia', city: 'Milano', flag: '🇮🇹', bookings: 134, revenue: 89200, averageStay: 2.8, coordinates: [45.4642, 9.1900] as [number, number] },
  { country: 'Francia', city: 'Parigi', flag: '🇫🇷', bookings: 98, revenue: 65400, averageStay: 4.1, coordinates: [48.8566, 2.3522] as [number, number] },
  { country: 'Germania', city: 'Berlino', flag: '🇩🇪', bookings: 87, revenue: 52300, averageStay: 3.7, coordinates: [52.5200, 13.4050] as [number, number] },
  { country: 'Spagna', city: 'Barcellona', flag: '🇪🇸', bookings: 76, revenue: 45600, averageStay: 3.9, coordinates: [41.3851, 2.1734] as [number, number] },
  { country: 'UK', city: 'Londra', flag: '🇬🇧', bookings: 65, revenue: 78900, averageStay: 2.5, coordinates: [51.5074, -0.1278] as [number, number] },
  { country: 'Olanda', city: 'Amsterdam', flag: '🇳🇱', bookings: 54, revenue: 34200, averageStay: 2.9, coordinates: [52.3676, 4.9041] as [number, number] },
  { country: 'USA', city: 'New York', flag: '🇺🇸', bookings: 43, revenue: 67800, averageStay: 4.2, coordinates: [40.7128, -74.0060] as [number, number] }
]; 